/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import BottomPartForm from './BottomPartForm';
import { LPBAdvancedModel } from './Models';
import { PAYMENT_METHOD, RECIPIENT_TYPES } from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import { amountFormattedValue, prepareFieldErrors, strToFloat } from 'services/utils';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';

const LPBAdvancedForm = ({
  customerNumber,
  calculateCommission,
  checkTransfer,
  checkIban,
  iban,
  setIban,
  isIbanCheckLoading,
  uploadDocuments,
  validationSchema,
  account,
  paymentMethod,
  uploadedFiles,
  commission,
  removePaymentFile,
  error,
  setError,
  isCommissionLoading,
  isLoading,
  isSuccess,
  isRepeatPayment,
  ibanCredentials,
  commonForm,
  countries,
  isFormResetAfterAccountChanging,
  setIsPreparedTransactionsCreation,
  isForApprovalTransactionEdit,
  isUserCanCreatePreparedTransaction,
  isUserCanSendTransaction,
  validateForApprovalTransaction
}) => {
  const i18n = useContext(i18nContext);
  const model = LPBAdvancedModel;
  const form = useFormik(validationSchema);
  const serverFieldsErrors = prepareFieldErrors(i18n, error);
  const [disabled, setDisabled] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [showMethodCommission, setShowMethodCommission] = useState(false);
  const { values, errors, handleSubmit, handleChange, validateField, validateForm, resetForm, setFieldValue } =
    commonForm || form;

  useEffect(() => {
    if (isRepeatPayment) {
      const asyncOperations = async () => {
        await handleIbanComplete(false);
      };

      asyncOperations();
    }
  }, [account.wallet_number]);

  useEffect(() => {
    if (isSuccess) {
      resetForm({});
      setDisabled(true);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (iban && !values.recipient_wallet) {
      setFieldValue('recipient_wallet', iban);
    }
  }, [iban, values.recipient_wallet]);

  useEffect(() => {
    const asyncOperations = async () => {
      if (!isFormResetAfterAccountChanging) return;

      if ((isRepeatPayment && !values.recipient_wallet) || !isRepeatPayment) {
        if (values.amount && paymentMethod) {
          await calculateCommission(customerNumber, {
            walletNumber: account?.wallet_number,
            paymentMethod: paymentMethod,
            amount: strToFloat(values?.amount?.toString() || '')
          });
        }

        if (!isForApprovalTransactionEdit) {
          await handleCheckTransfer(undefined);
        }
      }
    };

    asyncOperations();
  }, [paymentMethod, isRepeatPayment, isFormResetAfterAccountChanging]);

  const handleRecipientTypeChange = (name, data) => handleChange({ target: { name, value: data } });

  const handleCheckTransfer = async (fieldName) => {
    if (fieldName) await validateField(fieldName);
    if (
      !serverFieldsErrors?.iban &&
      values.recipient_wallet &&
      (ibanCredentials?.bank_country || values.bank_country) &&
      (ibanCredentials?.bank_name || values.bank_name) &&
      (ibanCredentials?.account_number || values.account_number) &&
      (ibanCredentials?.bic || values.bic) &&
      values.registration_number &&
      values.recipient_type &&
      (values.recipient_type === RECIPIENT_TYPES.COMPANY
        ? values.company_name
        : values.first_name && values.last_name) &&
      values.registration_number &&
      values.beneficiary_city &&
      values.beneficiary_country &&
      values.beneficiary_post_code &&
      values.beneficiary_address &&
      values.amount &&
      values.comment
    ) {
      setDisabled(false);
      setShowMethodCommission(true);
      const errorsResult = await validateForm();
      if (!Object.keys(errorsResult).length) {
        await checkTransfer(
          customerNumber,
          model({
            providerTableData: values,
            data: { wallet: account, paymentMethod, ...ibanCredentials }
          })
        );
      }
    }
  };

  const onAmountIBANChange = (e) => {
    setShowMethodCommission(false);
    handleChange(e);
    if (e.target.name === 'recipient_wallet') {
      setIban(e.target.value);
    }
  };

  const getCommissionValue = () => {
    if (!commission.value && !commission.currency) {
      return '0.00';
    }
    return `${commission.commissionDetails?.displayAmount || amountFormattedValue(commission.value)}`;
  };

  const handleIbanComplete = async (causedOnBlur) => {
    if (causedOnBlur) await validateField('recipient_wallet');
    values.recipient_wallet && (await checkIban(values.recipient_wallet));
    if (causedOnBlur && !isForApprovalTransactionEdit) await handleCheckTransfer(undefined);
  };

  const bottomPartProps = {
    values,
    errors,
    error,
    handleChange,
    handleSubmit,
    serverFieldsErrors,
    onAmountIBANChange,
    calculateCommission,
    handleCheckTransfer,
    getCommissionValue,
    paymentMethod,
    isSubmitDisabled:
      (isForApprovalTransactionEdit ? false : disabled) ||
      isLoading ||
      isCommissionLoading ||
      !!Object.keys(errors).length ||
      !!Object.keys(serverFieldsErrors).length ||
      !!error ||
      !paymentMethod,
    isLoading: isLoading || isCommissionLoading,
    uploadedFiles,
    uploadDocuments,
    removePaymentFile,
    customerNumber,
    currentWalletNumber: account?.wallet_number,
    commission,
    setIsPreparedTransactionsCreation,
    isForApprovalTransactionEdit,
    isUserCanCreatePreparedTransaction,
    isUserCanSendTransaction,
    validateForApprovalTransaction,
    model: LPBAdvancedModel,
    setError
  };

  return (
    <>
      <Input
        isRequired={true}
        autoComplete={'off'}
        label={i18n.getMessage('transfer.form.recipientIban.label')}
        name={'recipient_wallet'}
        value={values.recipient_wallet}
        error={errors.recipient_wallet || serverFieldsErrors.iban || serverFieldsErrors.recipientIban}
        onChange={onAmountIBANChange}
        onBlur={() => handleIbanComplete(true)}
        subText={
          ibanCredentials?.bank_name &&
          i18n.getMessage('transfer.form.bankName.subText', { bankName: ibanCredentials?.bank_name })
        }
      />
      {(paymentMethod === PAYMENT_METHOD.SWIFT || paymentMethod === PAYMENT_METHOD.TARGET2) && (
        <>
          <div className={'inputs-wrapper-evenly'}>
            <InputDropDownSearch
              isRequired={true}
              isDisabled={isIbanCheckLoading || !!ibanCredentials?.bank_country}
              autoComplete={'off'}
              label={i18n.getMessage('transfer.form.bankCountry.label')}
              name={'bank_country'}
              value={ibanCredentials?.bank_country || values.bank_country}
              onChange={handleRecipientTypeChange}
              options={countries.map((value) => ({ key: value, value: i18n.getMessage(`country.${value}`) }))}
              error={errors.bank_country || serverFieldsErrors.bank_country}
              onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('bank_country')}
            />
            <Input
              isRequired={true}
              autoComplete={'off'}
              isDisabled={isIbanCheckLoading || !!ibanCredentials?.bank_name}
              label={i18n.getMessage('transfer.form.bankName.label')}
              name={'bank_name'}
              value={ibanCredentials?.bank_name || values.bank_name}
              onChange={handleChange}
              error={errors.bank_name || serverFieldsErrors.bank_name}
              onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('bank_name')}
            />
          </div>
          <div className={'inputs-wrapper-evenly'}>
            <Input
              isRequired={true}
              autoComplete={'off'}
              isDisabled={isIbanCheckLoading || !!ibanCredentials?.account_number}
              label={i18n.getMessage('transfer.form.accountNumber.label')}
              name={'account_number'}
              value={ibanCredentials?.account_number || values.account_number}
              onChange={handleChange}
              error={errors.account_number || serverFieldsErrors.account_number}
              onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('account_number')}
            />
            <Input
              isRequired={true}
              autoComplete={'off'}
              isDisabled={isIbanCheckLoading || !!ibanCredentials?.bic}
              label={i18n.getMessage('transfer.form.BIC.label')}
              error={errors?.bic || serverFieldsErrors?.bic}
              name={'bic'}
              value={ibanCredentials?.bic || values.bic}
              onChange={handleChange}
              onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('bic')}
            />
          </div>
        </>
      )}
      <div className={`inputs-wrapper ${values.recipient_type}`}>
        <InputDropDown
          label={i18n.getMessage('transfer.form.recipientType.label')}
          autoComplete={'off'}
          name={'recipient_type'}
          value={values.recipient_type}
          onChange={handleRecipientTypeChange}
          options={Object.values(RECIPIENT_TYPES).map((value) => ({ key: value, value }))}
          isDisabled={isIbanCheckLoading}
        />
        {values.recipient_type === RECIPIENT_TYPES.COMPANY && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.companyName.label')}
            name={'company_name'}
            value={values.company_name}
            error={errors.company_name || serverFieldsErrors.companyName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('company_name')}
            isDisabled={isIbanCheckLoading}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.firstName.label')}
            name={'first_name'}
            value={values.first_name}
            error={errors.first_name || serverFieldsErrors.firstName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('first_name')}
            isDisabled={isIbanCheckLoading}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.lastName.label')}
            name={'last_name'}
            value={values.last_name}
            error={errors.last_name || serverFieldsErrors.lastName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('last_name')}
            isDisabled={isIbanCheckLoading}
          />
        )}
      </div>
      <div className={'inputs-wrapper-evenly'}>
        <Input
          isRequired={true}
          autoComplete={'off'}
          label={
            values.recipient_type === RECIPIENT_TYPES.COMPANY
              ? i18n.getMessage('transfer.form.registrationNumber.label')
              : i18n.getMessage('transfer.form.identificationNumber.label')
          }
          name={'registration_number'}
          value={values.registration_number}
          onChange={handleChange}
          error={errors.registration_number || serverFieldsErrors.registration_number}
          onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('registration_number')}
          isDisabled={isIbanCheckLoading}
        />
        <Input
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.beneficiaryCity.label')}
          name={'beneficiary_city'}
          value={values.beneficiary_city}
          onChange={handleChange}
          error={errors.beneficiary_city || serverFieldsErrors.beneficiary_city}
          onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('beneficiary_city')}
          isDisabled={isIbanCheckLoading}
        />
      </div>
      <div className={'inputs-wrapper-evenly'}>
        <InputDropDownSearch
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.beneficiaryCountry.label')}
          name={'beneficiary_country'}
          value={values.beneficiary_country}
          onChange={handleRecipientTypeChange}
          options={countries.map((value) => ({ key: value, value: i18n.getMessage(`country.${value}`) }))}
          error={errors.beneficiary_country || serverFieldsErrors.beneficiary_country}
          onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('beneficiary_country')}
          isDisabled={isIbanCheckLoading}
        />
        <Input
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.beneficiaryPostalCode.label')}
          name={'beneficiary_post_code'}
          value={values.beneficiary_post_code}
          onChange={handleChange}
          error={errors.beneficiary_post_code || serverFieldsErrors.beneficiary_post_code}
          onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('beneficiary_post_code')}
          isDisabled={isIbanCheckLoading}
        />
      </div>
      <Input
        isRequired={true}
        autoComplete={'off'}
        label={i18n.getMessage('transfer.form.beneficiaryAddress.label')}
        name={'beneficiary_address'}
        value={values.beneficiary_address}
        onChange={handleChange}
        error={errors.beneficiary_address || serverFieldsErrors.beneficiary_address}
        onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('beneficiary_address')}
        isDisabled={isIbanCheckLoading}
      />
      <BottomPartForm {...bottomPartProps} />
    </>
  );
};

LPBAdvancedForm.propTypes = {
  customerNumber: PropTypes.string,
  calculateCommission: PropTypes.func.isRequired,
  checkTransfer: PropTypes.func.isRequired,
  uploadDocuments: PropTypes.func.isRequired,
  checkIban: PropTypes.func.isRequired,
  iban: PropTypes.string,
  setIban: PropTypes.func,
  isIbanCheckLoading: PropTypes.bool,
  ibanCredentials: PropTypes.object,
  validationSchema: PropTypes.object,
  account: PropTypes.object,
  paymentMethod: PropTypes.string,
  commission: PropTypes.object,
  isCommissionLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  isRepeatPayment: PropTypes.bool,
  error: PropTypes.any,
  setError: PropTypes.func,
  uploadedFiles: PropTypes.array,
  removePaymentFile: PropTypes.func,
  commonForm: PropTypes.object,
  currency: PropTypes.string,
  isSuccess: PropTypes.bool,
  countries: PropTypes.array,
  isFormResetAfterAccountChanging: PropTypes.bool,
  setIsPreparedTransactionsCreation: PropTypes.func,
  isForApprovalTransactionEdit: PropTypes.bool,
  isUserCanCreatePreparedTransaction: PropTypes.bool,
  isUserCanSendTransaction: PropTypes.bool,
  validateForApprovalTransaction: PropTypes.func
};

export default LPBAdvancedForm;
